import PAImage from "component/design-system/images/PAImage";
import PAText from "component/design-system/typography/PAText/PAText";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import Testimonials from "modules/home/testimonials/Testimonials";
import NoSsr from "modules/NoSsr";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { numberFormat } from "util/helper/numberFormat";
import HideIf from "util/HideIf";
import styles from "./about-us.module.scss";
import LearnMore from "./LearnMore";

const timelineData = [
  {
    period: "2015-2016",
    title: "Founding and Initial Launch",
    description: "Founded in 2015, Parts Avatar launched its first online store in 2016 with 30,000 parts.",
    image: "",
  },
  {
    period: "2017-2018",
    title: "Canada-wide Shipping",
    description: "We started delivering orders across Canada in 2017. By then our catalog had grown to 250,000+ parts.",
    image: "",
  },
  {
    period: "2019-2022",
    title: "24/7 Live Expert Help and Local Deliveries",
    description: "In 2019 we began assigning dedicated live experts online to help find the right parts, verify fitment, and resolve issues. We started offering rapid deliveries in GTA in 2021.",
    image: "",
  },
  {
    period: "2023-2024",
    title: "Catalog Expansion and Improvements",
    description: "We started offering rapid deliveries with our own fleet in the GTA area in 2021, which has now expanded significantly.",
    image: "",
  },
  {
    period: "2025 AND BEYOND",
    title: "Canada's #1 Auto Parts Store",
    description: "We are proud to have served 1 million+ Canadians and in helping them repair their cars for less. Being one of the most loved auto parts store in Canada has made this journey fulfilling for the entire Parts Avatar team.",
    image: "",
  },
];

function AboutUsContainer(props) {
  const { googleReviews } = props;
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const router = useRouter();
  const { t } = useTranslation("static-pages");
  const timelineItemRefs = useRef([]);
  const [isMobile] = useScreenSize();
  const { averageRating, totalReviewCount } = useSelector((store: RootState) => store.authenticationState);

  useEffect(() => {
    const elements = document.getElementsByClassName("trustpilot-widget");
    Array.from(elements).forEach((element) => {
      window.Trustpilot?.loadFromElement(element);
    });
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.animate);
        }
      });
    }, observerOptions);

    timelineItemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      timelineItemRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [timelineData]);

  return (
    <div className={styles.about_us_container}>
      <div className={`row ${styles.section} ${styles.about_section}`}>
        <div
          className="col-lg-7 col-md-12"
          onClick={() => setIframeLoaded(true)}
        >
          <HideIf show={!isIframeLoaded}>
            <div className={styles.about_section_image}>
              <PAImage
                src={getRelativeImagePath("/cms/pubicImages/images/about-us-video.webp")}
                height={454}
                className={styles.video_thumbnail}
                alt="About Us"
              />
              <i className={`fa fa-play ${styles.play_icon}`} />
            </div>
          </HideIf>

          <HideIf show={isIframeLoaded}>
            <div className={styles.about_section_video}>
              <iframe
                title="Video Player"
                allowFullScreen
                src="https://www.youtube.com/embed/pCrcm15eSWQ"
                data-clarity-mask="True"
              />
            </div>
          </HideIf>
        </div>
        <div className="col-lg-5 col-md-12">
          <PAText
            component="h1"
            bold
            color="black"
            className={`${styles.section_header} ${styles.about_section_header}`}
          >
            {t("About us")}
          </PAText>
          <PAText component="p" className={styles.about_section_text}>
            {t("about_us_1")}
          </PAText>
          <PAText component="p" className={styles.about_section_text}>
            {t("about_us_2")}
          </PAText>
          <PAText component="p" className={styles.about_section_text}>
            {t("about_us_3")}
          </PAText>
        </div>
      </div>

      <div className={`${styles.section} ${styles.quote_section}`}>
        <div className={styles.quote_section_container}>
          <div className={styles.quotation_mark}>
            <PAText>“</PAText>
          </div>
          <PAText component="p" className={styles.quote}>
            {t("about_quote_1")}
          </PAText>
          <PAText component="p" className={styles.author}>
            {t(`SHERDEEP SINGH - ${t("FOUNDER & CEO")}`)}
          </PAText>
          <PAText className="text-center">{t("about_quote_2")}</PAText>
          <div
            className={styles.shop_canadian}
            onClick={() => router.push("/")}
          >
            <PAImage
              src={getRelativeImagePath("/images/public/canadian-about-PA.webp")}
              alt="Proudly Made in Canada"
              width={25}
              height={25}
            />
            <PAText bold>{t("SHOP CANADIAN")}</PAText>
          </div>
        </div>
      </div>

      <div className={`${styles.section} ${styles.text_image_section} ${styles.canadian_section}`}>
        <div className={styles.text_left}>
          <PAText
            component="h2"
            bold
            color="black"
            className={`${styles.section_header} ${styles.canadian_section_header}`}
          >
            {t("By Canadians,")}
          </PAText>
          <PAText
            component="h2"
            bold
            color="black"
            className={`${styles.section_header} ${styles.canadian_section_header}`}
          >
            {t("For Canadians")}
          </PAText>
          <PAText
            component="p"
            className={`${styles.canadian_section_text} m-t-30`}
          >
            {t("about_canadian_1")}
          </PAText>
          <PAText component="p" className={styles.canadian_section_text}>
            {t("about_canadian_2")}
          </PAText>
        </div>
        <div className={styles.image}>
          <PAImage
            src={getRelativeImagePath("/cms/pubicImages/images/about-us-team.webp")}
            width={460}
            height={460}
            alt="Parts Avatar team"
          />
        </div>
      </div>

      <div className={`${styles.section} ${styles.quickfacts_section}`}>
        <PAText
          component="h2"
          bold
          color="black"
          className={styles.section_header}
        >
          {t("Quick Facts")}
        </PAText>
        <div className={styles.quick_facts}>
          <div className={styles.quickfact}>
            <PAText className={styles.quickfact_large}>1 Million+</PAText>
            <PAText bold className={styles.quickfact_sub}>
              {t("CANADIANS TRUST")}
            </PAText>
            <PAText className={styles.quickfact_description}>
              {t("Parts Avatar for their auto parts needs")}
            </PAText>
          </div>
          <div className={styles.quickfact}>
            <PAText className={styles.quickfact_large}>20,000+</PAText>
            <PAText bold className={styles.quickfact_sub}>
              5
              <i className="fa fa-star" />
              {t("REVIEWS")}
            </PAText>
            <PAText className={styles.quickfact_description}>
              {t("On Google and Trustpilot - the most trusted review platforms")}
            </PAText>
          </div>
          <div className={styles.quickfact}>
            <PAText className={styles.quickfact_large}>2.5 Million+</PAText>
            <PAText bold className={styles.quickfact_sub}>
              {t("AUTO PARTS")}
            </PAText>
            <PAText className={styles.quickfact_description}>
              {t("are available for sale on Parts Avatar")}
            </PAText>
          </div>
          <div className={styles.quickfact}>
            <PAText className={styles.quickfact_large}>400+</PAText>
            <PAText bold className={styles.quickfact_sub}>
              {t("BRANDS")}
            </PAText>
            <PAText className={styles.quickfact_description}>
              {t("to choose from for car parts and accessories")}
            </PAText>
          </div>
          <div className={styles.quickfact}>
            <PAText className={styles.quickfact_large}>60%</PAText>
            <PAText bold className={styles.quickfact_sub}>
              {t("AVERAGE SAVINGS")}
            </PAText>
            <PAText className={styles.quickfact_description}>
              {t("when compared to buying from local stores")}
            </PAText>
          </div>
          <div className={styles.quickfact}>
            <PAText className={styles.quickfact_large}>99.8%+</PAText>
            <PAText bold className={styles.quickfact_sub}>
              {t("CATALOG ACCURACY")}
            </PAText>
            <PAText className={styles.quickfact_description}>
              {t("ensuring the parts you choose fit your car")}
            </PAText>
          </div>
        </div>
      </div>

      <div
        className={`${styles.section} ${styles.fullwidth_section} ${styles.movement_section}`}
      >
        <div className={styles.fullwidth_container}>
          <div className={styles.movement_section_header}>
            <PAImage
              src={getRelativeImagePath("/images/public/rightToRepair-about-PA.webp")}
              alt="Proudly Made in Canada"
              width={70}
              height={70}
            />
            <PAText
              component="h2"
              bold
              color="black"
              className={styles.section_header}
            >
              {t("Championing the Right to Repair Movement")}
            </PAText>
          </div>

          <div className={styles.right_to_repair}>
            <PAText>{t("about_right_to_repair")}</PAText>
          </div>
        </div>
      </div>

      <div className={`${styles.section} ${styles.timeline_section}`}>
        <PAText
          component="h2"
          bold
          color="black"
          className={styles.section_header}
        >
          {t("Parts Avatar Over The Years")}
        </PAText>
        <PAText>{t("EST 2015")}</PAText>
        <div className={styles.timeline_container}>
          <div className={styles.timeline}>
            {timelineData.map((item, index) => (
              <div
                className={`${styles.timeline_item} ${styles.hidden}`}
                key={item.period}
                ref={(el) => (timelineItemRefs.current[index] = el)}
              >
                <div className={styles.timeline_left}>
                  <PAImage
                    src={getRelativeImagePath(`/cms/pubicImages/images/timeline-${index}.webp`)}
                    alt="timeline"
                    width={200}
                    height={200}
                  />
                </div>

                <div className={styles.timeline_line}>
                  <div className={styles.dot} />
                </div>

                <div className={styles.timeline_right}>
                  <PAText bold className={styles.period}>
                    {t(item.period)}
                  </PAText>
                  <PAText component="h2" className={styles.title}>{t(item.title)}</PAText>
                  <PAText className={styles.description}>{t(item.description)}</PAText>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        className={`${styles.section} ${styles.fullwidth_section} ${styles.reviews_section}`}
      >
        <div
          className={`${styles.fullwidth_container} ${styles.reviews_section_container}`}
        >
          <PAText
            component="h2"
            color="black"
            bold
            className={styles.section_header}
          >
            {t("Real Car Owners. Real Reviews.")}
          </PAText>

          <Testimonials
            reviews={googleReviews}
            tileWidth={isMobile ? 300 : 500}
            translationFile="static-pages"
          />

          <div className={styles.review_images}>
            <div
              className={`${styles.review_image} d-flex align-items-center`}
              onClick={() => window.open("https://www.google.com/search?q=partsavatar+mississauga&oq=partsavatar+mis&gs_lcrp=EgZjaHJvbWUqCggAEAAY4wIYgAQyCggAEAAY4wIYgAQyDQgBEC4YrwEYxwEYgAQyBggCEEUYOTIHCAMQABiABDIHCAQQABiABDIHCAUQABiABDIHCAYQABiABDIGCAcQRRg8qAIAsAIA&sourceid=chrome&ie=UTF-8#lrd=0x89d4d25e5de0fd57:0x3a54a7d133a39b9d,1,,,,", "_blank")}
            >
              <PAImage
                width={50}
                height={50}
                alt="Discover Our Products Through Customer Reviews"
                src="https://banner2.cleanpng.com/20240216/sb/transparent-google-logo-google-logo-with-multicolored-g-and-1710875781697.webp"
                className="p-r-5"
              />
              <div className="d-flex flex-column">
                <PAText inlineBlock color="alert-secondary">
                  {Array.from({ length: 5 }, () => (
                    <i className={`fa fa-star ${styles.star}`} />
                  ))}
                </PAText>
                <span>
                  {/* {`${infoData?.overallRating} stars | ${infoData?.totalReviews} reviews`} */}
                  {t("reviews_rating", { starRating: averageRating, reviewCount: numberFormat(totalReviewCount) })}
                </span>
              </div>
            </div>
            <div
              className={styles.review_image}
              onClick={() => window.open("https://www.trustpilot.com/review/partsavatar.ca")}
            >
              <NoSsr>
                <div
                  className="trustpilot-widget"
                  data-locale="en-US"
                  data-template-id="53aa8807dec7e10d38f59f32"
                  data-businessunit-id="5dceae754be8620001ff60a0"
                  data-style-height="90px"
                  data-style-width="130px"
                >
                  <a href="https://www.trustpilot.com/review/partsavatar.ca" target="_blank" rel="noopener noreferrer">Trustpilot</a>
                </div>
              </NoSsr>
            </div>
          </div>
        </div>
      </div>

      <LearnMore />
    </div>
  );
}

export default AboutUsContainer;
